/**
 * ImportStatusDot — Tier 0 module-local 8 px colored dot for the
 * imports queue.
 *
 * Per `.design-incoming/admin-20260522/IMPORTS_SPEC.md §3`. Five
 * canonical statuses:
 *
 *   queued                 → `--fg-tertiary` (gray)
 *   running                → `--brand-500`  (brand)
 *   completed              → `--success-500` (green)
 *   completed_with_errors  → `--warning-500` (amber)
 *   failed                 → `--danger-500`  (red)
 *
 * **Color is never the sole conveyor** per
 * `ACCESSIBILITY.md §4` — every render site MUST pair this dot
 * with adjacent text that names the status. The component asserts
 * this contract via `aria-label` so screen readers always read the
 * status name, but the visible text-pairing is the caller's
 * responsibility.
 *
 * Implemented as a plain styled `<span>` rather than composing
 * kit `Badge` — a single colored dot is too narrow a surface for
 * the kit's variant + size + icon matrix.
 */

import * as React from 'react';

export type ImportStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

const STATUS_LABELS: Record<ImportStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  failed: 'Failed',
};

const STATUS_COLORS: Record<ImportStatus, string> = {
  queued: 'var(--fg-tertiary)',
  running: 'var(--brand-500)',
  completed: 'var(--success-500)',
  completed_with_errors: 'var(--warning-500)',
  failed: 'var(--danger-500)',
};

export interface ImportStatusDotProps {
  readonly status: ImportStatus;
  /** Diameter in pixels; default 8 per IMPORTS_SPEC.md §3. */
  readonly size?: number;
}

export function ImportStatusDot({
  status,
  size = 8,
}: ImportStatusDotProps): React.ReactElement {
  return (
    <span
      role="img"
      aria-label={`Import status: ${STATUS_LABELS[status]}`}
      className="inline-block flex-shrink-0"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: 'var(--radius-full)',
        background: STATUS_COLORS[status],
      }}
    />
  );
}
